import React, { useState, useEffect } from "react";
import { Grid, MenuItem, Select } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { calculateConvertedQuantity } from "../../../utils/unitConversionUtils";

const useStyles = makeStyles({
    select: {
        color: "inherit",
        fontWeight: "inherit",
        "& .MuiSelect-icon": {
            color: "inherit",
        },
    },
});

/**
 * @COMPONENT_NAME GeneralUnitConversionDisplay
 * @DESCRIPTION 단위 변환 표시 컴포넌트
 *              원본 수량을 선택된 단위로 변환하여 표시하고 단위 선택 드롭다운을 제공
 *              선택된 목적에 따라 적절한 단위를 자동으로 결정하며
 *              이벤트 버블링 방지를 통한 안정적인 UI 상호작용을 보장
 *              자재별 단위 목록과 목적 목록을 메모이제이션하여 성능 최적화
 *
 * @PROPS
 *   - {Object} ctx - FlexGrid 셀 컨텍스트 (row, col, item, value 포함)
 *   - {Object} schema - 컬럼 스키마 정보 (name, header 등)
 *   - {Object} selectedPurpose - 선택된 단위 목적 객체 (unitPurposeId, purposeName 포함)
 *   - {Array} unitGroupDetailList - 단위 그룹 상세 리스트
 *   - {Array} unitPurposeList - 단위 목적 리스트
 *
 * @STATE
 *   - {Object} selectedUnit - 현재 선택된 단위 정보 (unitId, unitSymbol, conversionFactor 포함)
 *   - {string} displayText - 변환된 수량을 표시하는 텍스트
 *   - {Array} unitList - 해당 자재의 단위 리스트
 *   - {Array} purposeList - 해당 자재의 목적 리스트
 *
 * @CALLBACKS
 *   - {Function} handleUnitChange - 단위 선택 변경 시 호출되는 콜백
 *
 * @EXAMPLE
 *   <UnitConversionDisplay
 *     ctx={ctx - cellTemplate의 Context 객체}
 *     schema={schema - 컬럼 스키마 정보}
 *     selectedPurpose={selectedPurpose - 선택된 단위 용도 객체}
 *     unitGroupDetailList={unitGroupDetailList - 전체 단위 그룹 상세 리스트}
 *     unitPurposeList={unitPurposeList - 전체 단위 용도 리스트}
 *   />
 */
const GeneralUnitConversionDisplay = React.memo((props) => {
    const { ctx, schema, unitList } = props;
    const classes = useStyles();

    const [selectedUnit, setSelectedUnit] = useState(null);
    const [displayText, setDisplayText] = useState("0");

    const originalQuantity = ctx.item?.[schema?.name] || 0;

    useEffect(() => {
        if (!unitList || unitList.length === 0) {
            setSelectedUnit(null);
            setDisplayText(originalQuantity.toString());
            return;
        }
        const displayUnit = unitList.find((unit) => unit.conversionFactor === 1);
        if (displayUnit) {
            setSelectedUnit(displayUnit);
            const convertedQuantity = calculateConvertedQuantity(originalQuantity, displayUnit.conversionFactor);
            setDisplayText(convertedQuantity);
        }
    }, [unitList, originalQuantity]);

    const handleUnitChange = (e) => {
        const unitId = e.target.value;
        const newUnit = unitList.find((unit) => unit.unitId === unitId);

        if (newUnit) {
            setSelectedUnit(newUnit);
            const convertedQuantity = calculateConvertedQuantity(originalQuantity, newUnit.conversionFactor);
            setDisplayText(convertedQuantity);
        }
    };

    if (!unitList || unitList.length === 0) {
        return <span>{originalQuantity}</span>;
    }

    return (
        <Grid container direction="row" justifyContent="flex-start" alignItems="center" spacing={1}>
            <Grid xs={8} item>
                {displayText}
            </Grid>
            <Grid xs={4} item onClick={(e) => e.stopPropagation()}>
                <Select
                    fullWidth
                    value={selectedUnit?.unitId || ""}
                    onChange={handleUnitChange}
                    disableUnderline
                    className={classes.select}
                >
                    {unitList.map((unit) => (
                        <MenuItem key={unit.unitId} value={unit.unitId}>
                            {unit.unitSymbol}
                        </MenuItem>
                    ))}
                </Select>
            </Grid>
        </Grid>
    );
});

export default GeneralUnitConversionDisplay;
